import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import {
	type ClientPropertyFlatDetailResponseDTO,
	ClientPropertyRenovationEnum,
	ClientPropertyTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { PropertyDataCard } from "web_myplann/Portfolio/Components/PortfolioTools/PropertyDataCard";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import {
	getBuildingMaterial,
	getDisposition,
} from "web_myplann/Portfolio/Helpers/propertyBuildingAttributes";
import { useGetPropertyOptionalParams } from "web_myplann/Portfolio/Hooks/useGetPropertyOptionalParams";
import { DataCardPreset } from "web_myplann/Shared/Components/DataCard";
import { DataNote } from "web_myplann/Shared/Components/DataNote";
import { DataRow } from "web_myplann/Shared/Components/DataRow";
import {
	DataRowCard,
	type DataRowCardItem,
} from "web_myplann/Shared/Components/DataRowCard";
import { dateFormatter } from "web_myplann/Shared/Utils/dateFormatters";
import { unitFormatter } from "web_myplann/Shared/Utils/numberFormatters";

const PageResources = Resources.PortfolioDetail;

const propTypes = [
	{ key: "terrace", label: PageResources.Property.Terrace },
	{
		key: "loggia",
		label: PageResources.Property.Loggia,
	},
	{
		key: "balcony",
		label: PageResources.Property.Balcony,
	},
	{
		key: "garage",
		label: PageResources.Property.Garage,
	},
	{
		key: "cellar",
		label: PageResources.Property.Cellar,
	},
	{
		key: "elevator",
		label: PageResources.Property.Elevator,
	},
	{
		key: "barrierFree",
		label: PageResources.Property.BarrierFree,
	},
	{
		key: "swimmingPool",
		label: PageResources.Property.SwimmingPool,
	},
	{
		key: "parkingSpace",
		label: PageResources.Property.ParkingSpace,
	},
	{
		key: "furnished",
		label: PageResources.Property.Furnished,
	},
];

type Props = {
	data: ClientPropertyFlatDetailResponseDTO;
	currencySign: string;
	documentsId: number;
};

export const Flat: FC<Props> = ({ data, currencySign, documentsId }) => {
	const { t } = useResource();

	const { data: documents } = usePortfolioDocumentsQuery(documentsId);
	const { properties } = useGetPropertyOptionalParams(propTypes, data, t);

	const buildingMaterial = getBuildingMaterial(data, t);
	const disposition = getDisposition(data, t);

	const renovation =
		data?.renovation && data.renovation !== ClientPropertyRenovationEnum.Null
			? t(PageResources.Property.RenovationValue[data.renovation])
			: "";

	const ownership = data?.ownership
		? t(PageResources.Property.OwnershipValue[data.ownership])
		: "";

	const basicInfoItems: DataRowCardItem[] = [
		{
			label: t(PageResources.Property.StreetAndNumber),
			value: data.location,
		},
		{
			label: t(PageResources.Property.Municipality),
			value: data.locationMunicipality,
		},
		{
			label: t(PageResources.Property.ZipCode),
			value: data.locationZipCode,
		},
		{
			label: t(PageResources.Property.ApartmentNumber),
			value: data.apartmentNumber,
		},
	];

	const propertyParametersItems: DataRowCardItem[] = [
		{
			label: t(PageResources.Property.Disposition),
			value: disposition,
		},
		{
			label: t(PageResources.Property.Floors),
			value: data.numberOfAboveGroundFloors,
		},
		{
			label: t(PageResources.Property.BuildingMaterial),
			value: buildingMaterial,
		},
		{
			label: t(PageResources.Property.FloorArea),
			value: unitFormatter(data.floorArea),
		},
		{
			label: t(PageResources.Property.UsableArea),
			value: unitFormatter(data.usableArea),
		},
		{
			label: t(PageResources.Property.HouseholdArea),
			value: unitFormatter(data.householdArea),
		},
		{
			label: t(PageResources.Property.Ownership),
			value: ownership,
		},
		{
			label: t(PageResources.Property.Renovation),
			value: renovation,
		},
		{
			label: t(PageResources.Property.YearOfRenovation),
			value: data?.yearOfRenovation,
		},
		{
			type: "note",
			label: t(PageResources.Property.Properties),
			text: properties,
		},
	];

	const bottomComponent =
		data.purchaseDate || data.yearOfCompletion || data.description ? (
			<Stack>
				<DataRow
					label={t(PageResources.Property.PurchaseDate)}
					value={dateFormatter(data.purchaseDate)}
				/>
				<DataRow
					label={t(PageResources.Property.YearOfCompletion)}
					value={data?.yearOfCompletion}
				/>
				<DataNote
					label={t(PageResources.Property.AdditionalDescription)}
					text={data.description}
				/>
			</Stack>
		) : null;

	return (
		<Stack>
			<DataRowCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={bottomComponent}
				items={basicInfoItems}
			/>

			<PropertyDataCard
				reproductionValue={data.reproductionValue}
				interiorPrice={data.interiorValue}
				marketValue={data.marketValue}
				priceHistory={data.estimatedPriceHistoryList}
				currencySign={currencySign}
				type={ClientPropertyTypeEnum.Flat}
			/>

			<DataRowCard
				preset={DataCardPreset.PropertyParameters}
				items={propertyParametersItems}
			/>

			<RelatedSection
				documents={documents?.data}
				related={data.related}
				portfolioId={data.id}
			/>
		</Stack>
	);
};
